import React, { useState, useMemo } from "react";
import { Card, Badge, Button, Form, Row, Col } from "react-bootstrap";
import toast from "react-hot-toast";

// 🌟 Mock Static Alerts Data
const mockAlerts = [
  {
    id: "101",
    title: "System Maintenance Alert",
    message: "Servers will undergo routine maintenance at 2:00 AM UTC. Expect 15 minutes of downtime.",
    timestamp: "2026-06-18 10:30 AM",
    isRead: false,
  },
  {
    id: "102",
    title: "High Memory Usage",
    message: "Node Server 3 is currently using 95% of its allocated memory limit.",
    timestamp: "2026-06-18 09:15 AM",
    isRead: false,
  },
  {
    id: "103",
    title: "New Admin Login",
    message: "A new login was detected from IP 192.168.1.45 (Bhavna Patel).",
    timestamp: "2026-06-17 06:45 PM",
    isRead: true,
  },
  {
    id: "104",
    title: "Failed Backup",
    message: "Database automated backup failed due to insufficient storage space.",
    timestamp: "2026-06-16 11:00 PM",
    isRead: true,
  },
];

const ReceiveAlerts = () => {
  const [alerts, setAlerts] = useState(mockAlerts);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all', 'unread', 'read'

  // 🌟 Filter alerts based on search input and read status
  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      const matchesSearch =
        alert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.message.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus =
        filterType === "all" ? true :
        filterType === "unread" ? !alert.isRead : alert.isRead;

      return matchesSearch && matchesStatus;
    });
  }, [alerts, searchQuery, filterType]);

  const unreadCount = alerts.filter((a) => !a.isRead).length;

  // 🌟 Action Handlers
  const handleMarkAsRead = (id) => {
    setAlerts((prev) =>
      prev.map((alert) => (alert.id === id ? { ...alert, isRead: true } : alert))
    );
  };

  const handleDelete = (id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    toast.success("Alert deleted successfully.");
  };

  const handleMarkAllAsRead = () => {
    setAlerts((prev) => prev.map((alert) => ({ ...alert, isRead: true })));
    toast.success("All alerts marked as read.");
  };

  return (
    <div className="container mt-4 mb-5"  >
      {/* 🌟 Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: "#E5484D", fontWeight: "bold" }}>
          System Alerts
          {unreadCount > 0 && (
            <Badge bg="danger" className="ms-3 fs-6 rounded-pill align-middle">
              {unreadCount} Unread
            </Badge>
          )}
        </h2>
        {unreadCount > 0 && (
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleMarkAllAsRead}
            style={{ borderRadius: "0.5rem" }}
          >
            Mark all as Read
          </Button>
        )}
      </div>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-4">
          
          {/* 🌟 Search & Filter Controls */}
          <Row className="mb-4">
            <Col md={8} className="mb-2 mb-md-0">
              <Form.Control
                type="text"
                placeholder="Search alerts by title or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ borderRadius: "0.5rem" }}
              />
            </Col>
            <Col md={4}>
              <Form.Select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                style={{ borderRadius: "0.5rem" }}
              >
                <option value="all">All Alerts</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </Form.Select>
            </Col>
          </Row>

          {/* 🌟 Alerts List */}
          <div className="d-flex flex-column gap-3">
            {filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert) => (
                <div 
                  key={alert.id} 
                  className="p-3 border rounded-3 position-relative"
                  style={{
                    backgroundColor: alert.isRead ? "#F8F9FA" : "#FFFFFF",
                    borderLeft: !alert.isRead ? "5px solid #E5484D" : "1px solid #dee2e6",
                    boxShadow: !alert.isRead ? "0 2px 5px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s ease"
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-1 text-black fw-bold">
                        {alert.title}
                        {!alert.isRead && (
                          <span 
                            className="ms-2 badge rounded-pill" 
                            style={{ backgroundColor: "#FFE4E6", color: "#E5484D", fontSize: "0.7rem" }}
                          >
                            New
                          </span>
                        )}
                      </h5>
                      <small className="text-muted">{alert.timestamp}</small>
                    </div>
                    
                    {/* Actions Dropdown / Buttons */}
                    <div className="d-flex gap-2">
                      {!alert.isRead && (
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="text-decoration-none text-success p-0"
                          onClick={() => handleMarkAsRead(alert.id)}
                        >
                          Mark Read
                        </Button>
                      )}
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="text-decoration-none text-muted p-0 ms-2 hover-danger"
                        onClick={() => handleDelete(alert.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <p className="mb-0 text-secondary" style={{ fontSize: "0.95rem" }}>
                    {alert.message}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center text-muted py-5">
                <h5 className="fw-bold">No alerts found</h5>
                <p className="mb-0">You're all caught up!</p>
              </div>
            )}
          </div>

        </Card.Body>
      </Card>
    </div>
  );
};

export default ReceiveAlerts;

/*
import React, { useState } from "react";
import {
  Card,
  Badge,
  Form,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import { FaBell, FaSearch } from "react-icons/fa";

const mockAlerts = [
  {
    id: 1,
    title: "Overdue Visit",
    message: "Property visit has been overdue for 3 days.",
    type: "warning",
    createdAt: "18 Jun 2026, 10:30 AM",
  },
  {
    id: 2,
    title: "New User Registered",
    message: "A new user has registered successfully.",
    type: "info",
    createdAt: "18 Jun 2026, 09:00 AM",
  },
  {
    id: 3,
    title: "Payment Failed",
    message: "Tenant payment transaction failed.",
    type: "danger",
    createdAt: "17 Jun 2026, 05:45 PM",
  },
];

const AlertPage = () => {
  const [search, setSearch] = useState("");

  const filteredAlerts = mockAlerts.filter(
    (alert) =>
      alert.title.toLowerCase().includes(search.toLowerCase()) ||
      alert.message.toLowerCase().includes(search.toLowerCase())
  );

  const getBadgeColor = (type) => {
    switch (type) {
      case "danger":
        return "danger";
      case "warning":
        return "warning";
      case "success":
        return "success";
      default:
        return "primary";
    }
  };

  return (
    <div className="container mt-4">
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3
              className="fw-bold mb-0"
              style={{ color: "#E5484D" }}
            >
              <FaBell className="me-2" />
              Alerts Center
            </h3>

            <Badge bg="danger" pill>
              {filteredAlerts.length} Alerts
            </Badge>
          </div>

          <InputGroup className="mb-4">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search alerts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          <Row>
            {filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert) => (
                <Col md={12} key={alert.id} className="mb-3">
                  <Card
                    className="border-0 shadow-sm"
                    style={{
                      borderLeft: `5px solid ${
                        alert.type === "danger"
                          ? "#dc3545"
                          : alert.type === "warning"
                          ? "#ffc107"
                          : "#0d6efd"
                      }`,
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="fw-bold">
                            {alert.title}
                          </h5>

                          <p className="text-muted mb-2">
                            {alert.message}
                          </p>

                          <small className="text-secondary">
                            {alert.createdAt}
                          </small>
                        </div>

                        <Badge bg={getBadgeColor(alert.type)}>
                          {alert.type.toUpperCase()}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <div className="text-center py-5">
                <FaBell
                  size={60}
                  className="text-muted mb-3"
                />
                <h5>No Alerts Found</h5>
              </div>
            )}
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AlertPage;*/